import { motion } from 'framer-motion'
import {
  Award,
  BarChart3,
  ChevronDown,
  Clock,
  Download,
  FileText,
  RefreshCw,
  Trash2,
  TrendingUp,
  Users
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Progress } from './ui/progress'

const Reports = () => {
  const [analytics, setAnalytics] = useState(null)
  const [templates, setTemplates] = useState([])
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(true)
  const [generatingReport, setGeneratingReport] = useState(null)
  const [downloadingReport, setDownloadingReport] = useState(null)
  const [exportingAll, setExportingAll] = useState(false)
  const [showExportOptions, setShowExportOptions] = useState(false)
  const [showFormatDropdown, setShowFormatDropdown] = useState({})
  const [clearingReports, setClearingReports] = useState(false)

  // API基礎URL
  const API_BASE = '/api/reports'

  // 獲取分析數據
  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/analytics`)
      if (response.ok) {
        const data = await response.json()
        setAnalytics(data.analytics)
      }
    } catch (error) {
      console.error('獲取分析數據失敗:', error)
    }
  }

  // 獲取報告範本
  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/templates`)
      if (response.ok) {
        const data = await response.json()
        setTemplates(data.templates)
      }
    } catch (error) {
      console.error('獲取報告範本失敗:', error)
    }
  }

  // 生成報告
  const generateReport = async (templateId, format = 'pdf') => {
    setGeneratingReport(templateId)
    try {
      const response = await fetch(`${API_BASE}/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          report_type: templateId,
          date_range: {
            start: '2025-08-01',
            end: '2025-08-31'
          },
          include_charts: true,
          format: format
        })
      })

      if (response.ok) {
        const data = await response.json()
        alert(`報告生成任務已提交！任務ID: ${data.task_id}`)
        // 重新載入報告列表
        setTimeout(() => {
          fetchReports()
        }, 3000) // 3秒後重新載入，給報告生成一些時間
      }
    } catch (error) {
      console.error('生成報告失敗:', error)
      alert('生成報告時發生錯誤')
    } finally {
      setGeneratingReport(null)
    }
  }

  // 獲取報告列表
  const fetchReports = async () => {
    try {
      const response = await fetch(`${API_BASE}/reports/list`)
      if (response.ok) {
        const data = await response.json()
        setReports(data.reports || [])
      }
    } catch (error) {
      console.error('獲取報告列表失敗:', error)
    }
  }

  // 匯出所有報告或生成綜合報告
  const exportAllReports = async () => {
    setExportingAll(true)
    try {
      // 生成綜合報告
      const response = await fetch(`${API_BASE}/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          report_type: 'comprehensive_summary',
          date_range: {
            start: '2025-01-01',
            end: '2025-12-31'
          },
          include_charts: true,
          include_all_data: true,
          format: 'pdf'
        })
      })

      if (response.ok) {
        const data = await response.json()
        alert(`綜合報告生成任務已提交！任務ID: ${data.task_id}`)
        // 重新載入報告列表
        setTimeout(() => {
          fetchReports()
        }, 3000)
      } else {
        alert('匯出失敗，請稍後再試')
      }
    } catch (error) {
      console.error('匯出報告失敗:', error)
      alert('匯出失敗，請稍後再試')
    } finally {
      setExportingAll(false)
      setShowExportOptions(false)
    }
  }

  // 批量下載所有報告
  const downloadAllReports = async () => {
    if (reports.length === 0) {
      alert('目前沒有可下載的報告')
      return
    }

    setExportingAll(true)
    try {
      // 逐一下載所有報告
      for (const report of reports) {
        await downloadReport(report.filename, false) // 不顯示個別下載狀態
        await new Promise(resolve => setTimeout(resolve, 500)) // 間隔500ms避免過載
      }
      alert(`已下載 ${reports.length} 個報告`)
    } catch (error) {
      console.error('批量下載失敗:', error)
      alert('批量下載失敗，請稍後再試')
    } finally {
      setExportingAll(false)
      setShowExportOptions(false)
    }
  }

  // 生成並直接下載報告
  const generateAndDownloadReport = async (templateId, format = 'pdf') => {
    setGeneratingReport(templateId)
    try {
      const response = await fetch(`${API_BASE}/reports/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          report_type: templateId,
          date_range: {
            start: '2025-08-01',
            end: '2025-08-31'
          },
          include_charts: true,
          format: format,
          auto_download: true
        })
      })

      if (response.ok) {
        const data = await response.json()

        // 等待報告生成完成（輪詢檢查）
        const checkReportStatus = async (taskId) => {
          let attempts = 0
          const maxAttempts = 60 // 最多等待60次（約3分鐘）

          const poll = async () => {
            try {
              await new Promise(resolve => setTimeout(resolve, 3000)) // 等待3秒

              const listResponse = await fetch(`${API_BASE}/reports/list`)
              const listData = await listResponse.json()

              // 查找最新生成的相同類型報告
              const latestReport = listData.reports
                .filter(report => report.filename.includes(templateId) ||
                  (templateId === 'monthly_summary' && report.filename.includes('月度總結')) ||
                  (templateId === 'patent_analysis' && report.filename.includes('專利分析')) ||
                  (templateId === 'performance' && report.filename.includes('效能')) ||
                  (templateId === 'client_insights' && report.filename.includes('客戶洞察')))
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]

              if (latestReport && format.toLowerCase() === latestReport.filename.split('.').pop().toLowerCase()) {
                // 找到匹配的報告，自動下載
                await downloadReport(latestReport.filename, false)
                alert(`${latestReport.filename} 已自動下載`)
                return true
              }

              attempts++
              if (attempts < maxAttempts) {
                return await poll()
              } else {
                alert('報告生成超時，請稍後在報告列表中查看')
                return false
              }
            } catch (error) {
              console.error('檢查報告狀態失敗:', error)
              return false
            }
          }

          return await poll()
        }

        await checkReportStatus(data.task_id)
      }
    } catch (error) {
      console.error('生成並下載報告失敗:', error)
      alert('生成並下載報告失敗，請稍後再試')
    } finally {
      setGeneratingReport(null)
    }
  }

  // 清除所有報告
  const clearAllReports = async () => {
    if (!window.confirm('確定要刪除所有報告檔案嗎？此操作無法復原。')) {
      return
    }

    setClearingReports(true)
    try {
      const response = await fetch(`${API_BASE}/reports/clear`, {
        method: 'DELETE'
      })

      if (response.ok) {
        alert('所有報告已清除')
        await fetchReports() // 重新載入報告列表
      } else {
        alert('清除報告失敗，請稍後再試')
      }
    } catch (error) {
      console.error('清除報告失敗:', error)
      alert('清除報告失敗，請稍後再試')
    } finally {
      setClearingReports(false)
    }
  }

  // 切換格式下拉選單
  const toggleFormatDropdown = (templateId, action) => {
    const key = `${templateId}_${action}`
    setShowFormatDropdown(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // 下載報告（修改為支援靜默下載）
  const downloadReport = async (filename, showStatus = true) => {
    if (showStatus) setDownloadingReport(filename)
    try {
      const response = await fetch(`${API_BASE}/reports/download/${filename}`)

      if (response.ok) {
        // 創建下載鏈接
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        if (showStatus) alert('下載失敗，請稍後再試')
      }
    } catch (error) {
      console.error('下載報告失敗:', error)
      if (showStatus) alert('下載失敗，請稍後再試')
    } finally {
      if (showStatus) setDownloadingReport(null)
    }
  }

  // 格式化文件大小
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // 獲取格式徽章樣式
  const getFormatBadgeStyle = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    switch (ext) {
      case 'pdf':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
      case 'xlsx':
      case 'xls':
        return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700'
      case 'json':
        return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700'
      case 'csv':
        return 'bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700'
    }
  }

  // 格式化日期
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('zh-TW')
  }

  // 初始化數據
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchAnalytics(), fetchTemplates(), fetchReports()])
      setLoading(false)
    }
    loadData()
  }, [])

  // 點擊外部關閉匯出選項
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportOptions && !event.target.closest('.export-options-container')) {
        setShowExportOptions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportOptions])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
          <span className="ml-2 text-gray-600 dark:text-gray-300">{'載入中...'}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
        >
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{'報告中心'}</h1>
            <p className="text-slate-700 dark:text-slate-300 mt-1 font-medium">{'生成和管理專利分析報告'}</p>
          </div>
          <div className="flex items-center space-x-3">
            <Badge variant="outline" className="text-violet-700 dark:text-violet-300 border-violet-300 dark:border-violet-600 bg-violet-50 dark:bg-violet-900/30 font-semibold">
              <TrendingUp className="h-3 w-3 mr-1" />
              {'智能分析報告'}
            </Badge>
            <div className="relative export-options-container">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-medium shadow-sm"
                onClick={() => setShowExportOptions(!showExportOptions)}
                disabled={exportingAll}
              >
                {exportingAll ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exportingAll ? '匯出中...' : '匯出報告'}
              </Button>

              {/* 匯出選項下拉選單 */}
              {showExportOptions && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                  <div className="p-3">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-2">匯出選項</h3>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={exportAllReports}
                        disabled={exportingAll}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        生成綜合報告
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                        onClick={downloadAllReports}
                        disabled={exportingAll || reports.length === 0}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        下載所有報告 ({reports.length})
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start text-gray-500"
                        onClick={() => setShowExportOptions(false)}
                      >
                        取消
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* 關鍵指標卡片 */}
        {analytics && analytics.patent_statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="flex items-center p-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{'總專利數'}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{analytics.patent_statistics?.total_patents ?? 0}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="flex items-center p-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                    <Award className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{'成功率'}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{analytics.patent_statistics?.success_rate ?? 0}%</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="flex items-center p-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{'本月報告'}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{reports.length}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }}>
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="flex items-center p-6">
                  <div className="flex items-center justify-center w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-lg">
                    <Users className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{'活躍用戶'}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{analytics.client_insights?.active_clients ?? 0}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* 報告範本 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md">
            <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
              <CardTitle className="text-slate-900 dark:text-slate-100 font-bold">{'報告模板'}</CardTitle>
              <CardDescription className="text-slate-700 dark:text-slate-300 font-medium">{'選擇預設模板快速生成報告'}</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {templates.map((template) => (
                  <div key={template.id} className="p-6 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all duration-200 shadow-sm bg-white dark:bg-slate-800/50">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg text-slate-900 dark:text-slate-100 mb-2">{template.name}</h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">{template.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-slate-500 dark:text-slate-400">
                          <span className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            {template.estimated_time}
                          </span>
                          <span className="flex items-center">
                            <FileText className="h-3 w-3 mr-1" />
                            {template.sections.length} 個部分
                          </span>
                        </div>
                      </div>
                      <Badge variant="outline" className="ml-4 text-xs font-medium border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800">
                        {template.type}
                      </Badge>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{'選擇預設模板快速生成報告'}:</p>
                      <div className="flex flex-wrap gap-1">
                        {template.sections.map((section, index) => (
                          <Badge key={index} variant="secondary" className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                            {section}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* 生成到列表和直接下載按鈕 */}
                      <div className="grid grid-cols-2 gap-3">
                        {/* 生成到列表按鈕組 */}
                        <div className="relative">
                          <div className="flex">
                            <Button
                              onClick={() => generateReport(template.id, 'pdf')}
                              disabled={generatingReport === template.id}
                              className="bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm flex-1 rounded-r-none"
                            >
                              {generatingReport === template.id ? (
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <FileText className="h-3 w-3 mr-1" />
                              )}
                              生成到列表
                            </Button>
                            <Button
                              onClick={() => toggleFormatDropdown(template.id, 'generate')}
                              disabled={generatingReport === template.id}
                              className="bg-blue-600 hover:bg-blue-700 text-white px-2 rounded-l-none border-l border-blue-500"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>

                          {/* 生成格式下拉選單 */}
                          {showFormatDropdown[`${template.id}_generate`] && (
                            <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                              <div className="p-2 space-y-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  onClick={() => {
                                    generateReport(template.id, 'pdf')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_generate`]: false }))
                                  }}
                                >
                                  <FileText className="h-3 w-3 mr-2" />
                                  PDF
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                  onClick={() => {
                                    generateReport(template.id, 'xlsx')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_generate`]: false }))
                                  }}
                                >
                                  <BarChart3 className="h-3 w-3 mr-2" />
                                  Excel
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  onClick={() => {
                                    generateReport(template.id, 'json')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_generate`]: false }))
                                  }}
                                >
                                  <Download className="h-3 w-3 mr-2" />
                                  JSON
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                  onClick={() => {
                                    generateReport(template.id, 'csv')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_generate`]: false }))
                                  }}
                                >
                                  <Download className="h-3 w-3 mr-2" />
                                  CSV
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* 直接下載按鈕組 */}
                        <div className="relative">
                          <div className="flex">
                            <Button
                              onClick={() => generateAndDownloadReport(template.id, 'pdf')}
                              disabled={generatingReport === template.id}
                              variant="outline"
                              className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-400 dark:hover:bg-emerald-900/20 font-medium text-sm flex-1 rounded-r-none"
                            >
                              {generatingReport === template.id ? (
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="h-3 w-3 mr-1" />
                              )}
                              直接下載
                            </Button>
                            <Button
                              onClick={() => toggleFormatDropdown(template.id, 'download')}
                              disabled={generatingReport === template.id}
                              variant="outline"
                              className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-600 dark:text-emerald-400 dark:hover:bg-emerald-900/20 px-2 rounded-l-none border-l-0"
                            >
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </div>

                          {/* 下載格式下拉選單 */}
                          {showFormatDropdown[`${template.id}_download`] && (
                            <div className="absolute top-full left-0 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50">
                              <div className="p-2 space-y-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                  onClick={() => {
                                    generateAndDownloadReport(template.id, 'pdf')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_download`]: false }))
                                  }}
                                >
                                  <FileText className="h-3 w-3 mr-2" />
                                  PDF
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                                  onClick={() => {
                                    generateAndDownloadReport(template.id, 'xlsx')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_download`]: false }))
                                  }}
                                >
                                  <BarChart3 className="h-3 w-3 mr-2" />
                                  Excel
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                  onClick={() => {
                                    generateAndDownloadReport(template.id, 'json')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_download`]: false }))
                                  }}
                                >
                                  <Download className="h-3 w-3 mr-2" />
                                  JSON
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full justify-start text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                  onClick={() => {
                                    generateAndDownloadReport(template.id, 'csv')
                                    setShowFormatDropdown(prev => ({ ...prev, [`${template.id}_download`]: false }))
                                  }}
                                >
                                  <Download className="h-3 w-3 mr-2" />
                                  CSV
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* 分析摘要 */}
        {analytics && analytics.patent_statistics && analytics.client_insights && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* 專利統計 */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md">
                <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <CardTitle className="text-slate-900 dark:text-slate-100 font-bold">{'統計數據'}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{'系統狀態'}</span>
                      <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{analytics.patent_statistics?.approved_patents ?? 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{'待處理案件'}</span>
                      <span className="text-lg font-bold text-amber-600 dark:text-amber-400">{analytics.patent_statistics?.pending_applications ?? 0}</span>
                    </div>
                    <div className="pt-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-slate-700 dark:text-slate-300">{'成功率'}</span>
                        <span className="font-bold text-slate-900 dark:text-slate-100">{analytics.patent_statistics?.success_rate ?? 0}%</span>
                      </div>
                      <Progress value={analytics.patent_statistics?.success_rate ?? 0} className="h-2 bg-slate-200 dark:bg-slate-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 客戶統計 */}
              <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md">
                <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <CardTitle className="text-slate-900 dark:text-slate-100 font-bold">{'客戶統計'}</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{'活躍客戶'}</span>
                      <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{analytics.client_insights?.active_clients ?? 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{'滿意度'}</span>
                      <span className="text-lg font-bold text-violet-600 dark:text-violet-400">{analytics.client_insights?.satisfaction_rate ?? 0}%</span>
                    </div>
                    <div className="pt-4">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="font-medium text-slate-700 dark:text-slate-300">{'客戶滿意度'}</span>
                        <span className="font-bold text-slate-900 dark:text-slate-100">{analytics.client_insights?.satisfaction_rate ?? 0}%</span>
                      </div>
                      <Progress
                        value={analytics.client_insights?.satisfaction_rate ?? 0}
                        className="h-2 bg-slate-200 dark:bg-slate-700"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* 已生成的報告列表 */}
        {reports.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 shadow-md">
              <CardHeader className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-slate-900 dark:text-slate-100 font-bold">已生成的報告</CardTitle>
                    <CardDescription className="text-slate-700 dark:text-slate-300 font-medium">
                      點擊下載按鈕可以將報告另存到您的電腦
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearAllReports}
                    disabled={clearingReports || reports.length === 0}
                    className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-600 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    {clearingReports ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    {clearingReports ? '清除中...' : '清除報告'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* 添加滾動容器 */}
                <div className="max-h-96 overflow-y-auto space-y-3 pr-2" style={{ scrollbarWidth: 'thin' }}>
                  {reports.length > 0 ? (
                    reports.map((report, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800/50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <div>
                            <p className="font-medium text-slate-900 dark:text-slate-100">
                              {report.report_type === 'monthly_summary' || report.report_type === 'monthly' ? '月度總結報告' :
                                report.report_type === 'client_summary' || report.report_type === 'client' ? '客戶摘要報告' :
                                  report.report_type === 'analysis' ? '專利分析報告' :
                                    report.report_type === 'performance' ? '效能報告' :
                                      report.report_type === 'business' ? '客戶洞察報告' :
                                        report.report_type === 'comprehensive_summary' || report.report_type === 'comprehensive' ? '綜合報告' :
                                          report.report_type}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {formatDate(report.created_at)} • {formatFileSize(report.size)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge className={`border ${getFormatBadgeStyle(report.filename)}`}>
                            {report.filename.split('.').pop().toUpperCase()}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => downloadReport(report.filename)}
                            disabled={downloadingReport === report.filename || exportingAll}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {downloadingReport === report.filename ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            <span className="ml-1">
                              {downloadingReport === report.filename ? '下載中...' : '下載'}
                            </span>
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                      <p>目前還沒有生成任何報告</p>
                      <p className="text-sm mt-1">請先使用上方的生成按鈕建立報告</p>
                    </div>
                  )}
                </div>

                {/* 刷新按鈕 */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button
                    variant="outline"
                    onClick={fetchReports}
                    className="w-full"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    刷新報告列表
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default Reports


